// Types, reference data and styles for the "Livraison Urbaine" module
// (urbain-console, urbain-kanban, urbain-modals).

export { money, kg, toFloat, parseFrDateTime, formatFrDateTime, PAY_STYLE } from "./envois-shared";

/* --------------------------------- Villes --------------------------------- */

export const URBAN_CITIES = [
  "Douala",
  "Yaoundé",
  "Garoua",
  "N'Gaoundéré",
  "Bafoussam",
  "Bamenda",
  "Kribi",
  "Limbé",
  "Bertoua",
  "Maroua",
];

// City code used in shipment numbers (LIV-2609-DLA-0321).
export const CITY_CODE: Record<string, string> = {
  Douala: "DLA",
  Yaoundé: "YDE",
  Garoua: "GOU",
  "N'Gaoundéré": "NGE",
  Bafoussam: "BFM",
  Bamenda: "BDA",
  Kribi: "KBI",
  Limbé: "LMB",
  Bertoua: "BTA",
  Maroua: "MVR",
};

/* ------------------------- Points de collecte / remise ------------------------- */

export type PointType = "Domicile" | "Point relais" | "Locker" | "Supermarché" | "Agence" | "Commerçant";

export const POINT_TYPES: PointType[] = ["Domicile", "Point relais", "Locker", "Supermarché", "Agence", "Commerçant"];

export const POINT_STYLE: Record<PointType, { bg: string; fg: string; abbr: string }> = {
  Domicile: { bg: "#fce7f3", fg: "#9d174d", abbr: "DOM" },
  "Point relais": { bg: "#ede9fe", fg: "#5b21b6", abbr: "PR" },
  Locker: { bg: "#cffafe", fg: "#155e75", abbr: "LK" },
  Supermarché: { bg: "#fef3c7", fg: "#92400e", abbr: "SM" },
  Agence: { bg: "#dbeafe", fg: "#1e40af", abbr: "AG" },
  Commerçant: { bg: "#dcfce7", fg: "#166534", abbr: "CM" },
};

export interface UrbanPoint {
  id: string;
  type: Exclude<PointType, "Domicile">;
  name: string;
  city: string;
  quartier: string;
  horaires: string;
  // Lockers: compartments; other points: parcels they can hold.
  capacity: number;
  used: number;
  // Handling fee charged when the point is used for collection or hand-over.
  fee: number;
}

export const URBAN_POINTS: UrbanPoint[] = [
  { id: "p1", type: "Agence", name: "Agence TCCE Akwa", city: "Douala", quartier: "Akwa", horaires: "Lun–Sam 07:30–19:00", capacity: 400, used: 212, fee: 0 },
  { id: "p2", type: "Locker", name: "Locker Carrefour Bonamoussadi", city: "Douala", quartier: "Bonamoussadi", horaires: "24h/24 · 7j/7", capacity: 48, used: 41, fee: 500 },
  { id: "p3", type: "Supermarché", name: "Super U Bonapriso", city: "Douala", quartier: "Bonapriso", horaires: "Tous les jours 08:00–21:00", capacity: 60, used: 22, fee: 500 },
  { id: "p4", type: "Point relais", name: "Relais Pharmacie du Littoral", city: "Douala", quartier: "Deïdo", horaires: "Lun–Sam 08:00–20:00", capacity: 30, used: 12, fee: 300 },
  { id: "p5", type: "Commerçant", name: "Boutique Mama Ngo — Marché Sandaga", city: "Douala", quartier: "Marché Sandaga", horaires: "Lun–Sam 07:00–18:00", capacity: 20, used: 17, fee: 300 },
  { id: "p6", type: "Locker", name: "Locker Total Logpom", city: "Douala", quartier: "Logpom", horaires: "24h/24 · 7j/7", capacity: 36, used: 36, fee: 500 },
  { id: "p7", type: "Agence", name: "Agence TCCE Mvog-Mbi", city: "Yaoundé", quartier: "Mvog-Mbi", horaires: "Lun–Sam 07:30–19:00", capacity: 350, used: 140, fee: 0 },
  { id: "p8", type: "Supermarché", name: "Santa Lucia Bastos", city: "Yaoundé", quartier: "Bastos", horaires: "Tous les jours 08:00–21:30", capacity: 50, used: 19, fee: 500 },
  { id: "p9", type: "Locker", name: "Locker Carrefour Emana", city: "Yaoundé", quartier: "Emana", horaires: "24h/24 · 7j/7", capacity: 40, used: 18, fee: 500 },
  { id: "p10", type: "Point relais", name: "Relais Librairie Omnisport", city: "Yaoundé", quartier: "Omnisport", horaires: "Lun–Sam 08:00–19:00", capacity: 25, used: 9, fee: 300 },
  { id: "p11", type: "Commerçant", name: "Quincaillerie Essos", city: "Yaoundé", quartier: "Essos", horaires: "Lun–Sam 07:30–18:30", capacity: 15, used: 4, fee: 300 },
  { id: "p12", type: "Agence", name: "Agence TCCE Garoua Centre", city: "Garoua", quartier: "Centre commercial", horaires: "Lun–Sam 08:00–18:00", capacity: 150, used: 61, fee: 0 },
  { id: "p13", type: "Point relais", name: "Relais Station Tradex Roumdé Adjia", city: "Garoua", quartier: "Roumdé Adjia", horaires: "Tous les jours 07:00–22:00", capacity: 25, used: 7, fee: 300 },
  { id: "p14", type: "Agence", name: "Agence TCCE N'Gaoundéré Gare", city: "N'Gaoundéré", quartier: "Gare", horaires: "Lun–Sam 08:00–18:00", capacity: 120, used: 38, fee: 0 },
  { id: "p15", type: "Commerçant", name: "Alimentation Dang", city: "N'Gaoundéré", quartier: "Dang", horaires: "Tous les jours 07:00–20:00", capacity: 15, used: 6, fee: 300 },
  { id: "p16", type: "Supermarché", name: "Supermarché Bafoussam Marché A", city: "Bafoussam", quartier: "Marché A", horaires: "Tous les jours 08:00–20:00", capacity: 40, used: 26, fee: 500 },
  { id: "p17", type: "Locker", name: "Locker Carrefour Mile 4", city: "Limbé", quartier: "Mile 4", horaires: "24h/24 · 7j/7", capacity: 24, used: 10, fee: 500 },
  { id: "p18", type: "Point relais", name: "Relais Kribi Plage", city: "Kribi", quartier: "Ngoyè", horaires: "Lun–Dim 09:00–19:00", capacity: 20, used: 5, fee: 300 },
];

/* --------------------------------- Statuts -------------------------------- */

export type UrbanService = "Livraison" | "Collecte" | "Collecte + livraison";

export type UrbanStatus =
  | "Demande reçue"
  | "Collecte planifiée"
  | "Collecté"
  | "En dépôt"
  | "En livraison"
  | "Disponible au point"
  | "Livré"
  | "Retiré"
  | "Échec de livraison"
  | "Retour expéditeur"
  | "Annulé";

export const STATUS_STYLE: Record<UrbanStatus, { bg: string; fg: string; dot: string }> = {
  "Demande reçue": { bg: "#fff7ed", fg: "#c2410c", dot: "#fb923c" },
  "Collecte planifiée": { bg: "#f3e8ff", fg: "#6b21a8", dot: "#a855f7" },
  Collecté: { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "En dépôt": { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "En livraison": { bg: "#e0f2fe", fg: "#0369a1", dot: "#0ea5e9" },
  "Disponible au point": { bg: "#ccfbf1", fg: "#0f766e", dot: "#14b8a6" },
  Livré: { bg: "#d1fae5", fg: "#047857", dot: "#10b981" },
  Retiré: { bg: "#d1fae5", fg: "#047857", dot: "#10b981" },
  "Échec de livraison": { bg: "#ffedd5", fg: "#c2410c", dot: "#f97316" },
  "Retour expéditeur": { bg: "#ffedd5", fg: "#9a3412", dot: "#ea580c" },
  Annulé: { bg: "#f1f5f9", fg: "#64748b", dot: "#94a3b8" },
};

export type UrbanStageKey =
  | "demande"
  | "paid"
  | "collecte"
  | "depot"
  | "livraison"
  | "point"
  | "delivered"
  | "failed"
  | "cancelled";

export const VEHICLES = ["Moto", "Tricycle", "Voiture", "Camionnette", "Vélo cargo"];

export const COURIERS = [
  "Mamadou Doumbia",
  "Paul Nsangou",
  "Alain Biboum",
  "Junior Mbarga",
  "Cédric Etoa",
  "Ousmanou Bello",
  "Hamadou Oumarou",
];

/* ---------------------------------- Item ---------------------------------- */

export interface UrbanItem {
  id: string;
  code: string;
  createdBy: string;
  createdAt: string;
  source: string;
  service: UrbanService;
  city: string;
  sender: string;
  senderPhone: string;
  senderKind: "Particulier" | "Commerçant" | "Entreprise";
  receiver: string;
  receiverPhone: string;
  pickupType: PointType;
  pickupPlace: string;
  pickupQuartier: string;
  dropType: PointType;
  dropPlace: string;
  dropQuartier: string;
  // Service date (ISO) and time window.
  date: string;
  creneau: string;
  colis: number;
  poids: number;
  nature: string;
  valeur: number;
  // Cash on delivery to collect from the recipient (0 = none).
  cod: number;
  codCollected: boolean;
  montant: number;
  paid: number;
  pay: "Payé" | "Non payé" | "Partiel" | "Remboursé";
  refOM: string;
  refTouch: string;
  canal: string;
  confirmed: boolean;
  coursier: string;
  vehicule: string;
  statut: UrbanStatus;
  statutInfo: string;
  codeRetrait: string;
  tentatives: number;
  audit: string;
  note: string;
  validePourLivraison?: boolean;
  quartierDestinationId?: number | null;
  quartierDestinationNom?: string;
  numericId?: number;
  // Board column, set once the item has been moved on the board; otherwise derived from its statuses.
  stage?: UrbanStageKey;
}

